/**
 * Shared formatting helpers for TCN module pages.
 * - Diamond amount → "4.12M" / "6.5K"
 * - USD → "$1,234"
 * - Date in Dubai TZ
 * - Encrypted value placeholder ("—")
 */

const MILLION = 1_000_000;
const THOUSAND = 1_000;

/**
 * Formats a number of diamonds compactly: 4_120_000 → "4.12M", 6_500 → "6.5K".
 * Returns "—" for null/undefined/NaN inputs (the placeholder we use whenever the
 * value comes from a `_enc` column that hasn't been decrypted client-side yet).
 */
export const formatDiamonds = (value) => {
  if (value === null || value === undefined) return '—';
  const n = Number(value);
  if (Number.isNaN(n)) return '—';
  const abs = Math.abs(n);
  if (abs >= MILLION) {
    const v = n / MILLION;
    return `${v.toFixed(v < 10 ? 2 : 1).replace(/\.0+$/, '')}M`;
  }
  if (abs >= THOUSAND) {
    const v = n / THOUSAND;
    return `${v.toFixed(v < 10 ? 1 : 0).replace(/\.0$/, '')}K`;
  }
  return `${Math.round(n)}`;
};

/**
 * Formats a USD amount: 1234 → "$1,234". Honours fractional cents only when small.
 */
export const formatUSD = (value, { fractionDigits = 0 } = {}) => {
  if (value === null || value === undefined) return '—';
  const n = Number(value);
  if (Number.isNaN(n)) return '—';
  return `$${n.toLocaleString('en-US', {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  })}`;
};

/**
 * Converts a diamond count to USD using metric_thresholds.diamond_to_usd_rate
 * (default 0.01 for MENA per D-040 / seeded by 20260427000000_tcn_helper_functions_and_seeds).
 *
 * Example: formatUsd(4_120_000, 0.01) → "$41,200"
 */
export const formatUsd = (diamonds, rate = 0.01, opts = {}) => {
  if (diamonds === null || diamonds === undefined) return '—';
  const d = Number(diamonds);
  const r = Number(rate);
  if (Number.isNaN(d) || Number.isNaN(r)) return '—';
  return formatUSD(d * r, opts);
};

/**
 * Formats a percentage: 72.5 → "72%", 100 → "100%".
 */
export const formatPct = (value, { decimals = 0 } = {}) => {
  if (value === null || value === undefined) return '—';
  const n = Number(value);
  if (Number.isNaN(n)) return '—';
  return `${n.toFixed(decimals)}%`;
};

/**
 * Formats an integer count (used for KPIs like creator count).
 */
export const formatInt = (value) => {
  if (value === null || value === undefined) return '—';
  const n = Number(value);
  if (Number.isNaN(n)) return '—';
  return Math.round(n).toLocaleString('en-US');
};

/**
 * Returns a date string in Dubai TZ for display ("26 Apr 2026").
 */
export const formatDubaiDate = (date, locale = 'en-AE') => {
  if (!date) return '';
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(locale, {
    timeZone: 'Asia/Dubai',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

/**
 * Returns the localized weekday + month + day line for the dashboard greeting
 * subtitle in Dubai TZ — e.g. "Tuesday, April 24". Mirrors the mockup
 * (module1-performance.jsx A1.1 TopBar subtitle).
 */
export const formatDubaiDayLine = (date, locale = 'en-AE') => {
  if (!date) return '';
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(locale, {
    timeZone: 'Asia/Dubai',
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  });
};

/**
 * Payment cycle = calendar month (TCN MTD logic). Returns { day, total } where
 * `day` is today's day-of-month and `total` the number of days in the month,
 * both in Dubai TZ — drives the "Day 14 of 30" cycle counter.
 */
export const dubaiCycleDay = (date = new Date()) => {
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return { day: 0, total: 0 };
  // sv-SE → "YYYY-MM-DD" in the requested TZ.
  const [y, m, day] = d
    .toLocaleDateString('sv-SE', { timeZone: 'Asia/Dubai' })
    .split('-')
    .map(Number);
  // new Date(y, m, 0): m is 1-based here, so index m = next month, day 0 = last
  // day of month m → number of days in the current month.
  const total = new Date(y, m, 0).getDate();
  return { day, total };
};

/**
 * Returns the current hour (0-23) in Dubai TZ — used to pick the
 * morning/afternoon/evening greeting.
 */
export const dubaiHour = (date = new Date()) => {
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return 0;
  const h = Number(
    new Intl.DateTimeFormat('en-US', {
      timeZone: 'Asia/Dubai',
      hour: 'numeric',
      hour12: false,
    }).format(d)
  );
  return Number.isNaN(h) ? 0 : h % 24;
};

/**
 * Returns "YYYYMMDD" key in Dubai TZ — used for the morning briefing cache.
 */
export const dubaiDayKey = (date = new Date()) => {
  const d = date instanceof Date ? date : new Date(date);
  // toLocaleDateString with sv-SE returns ISO-ish "YYYY-MM-DD"
  const iso = d.toLocaleDateString('sv-SE', { timeZone: 'Asia/Dubai' });
  return iso.replace(/-/g, '');
};

/**
 * Returns the previous Dubai-day "YYYYMMDD" key — used by the morning briefing to
 * read yesterday's `tcn_manager_daily_priority` row. Subtracting 24h on the JS
 * Date is safe because Dubai TZ doesn't observe DST.
 */
export const dubaiDayKeyYesterday = (date = new Date()) => {
  const d = date instanceof Date ? new Date(date.getTime()) : new Date(date);
  d.setHours(d.getHours() - 24);
  return dubaiDayKey(d);
};

/**
 * Placeholder shown when an encrypted column hasn't been decrypted yet.
 *
 * Phase A architecture:
 *   - manager-level financials (manager_current_month, manager_tier_current_month)
 *     are RLS-blocked from super_admin → mirrored as `*_clear` columns.
 *   - creator-level financials (creator_current_month) ALLOW super_admin SELECT,
 *     so they remain `*_enc` only and are decrypted on demand by the Edge
 *     Function `tcn-decrypt-creator` (used by Module 4 detail page).
 *
 * The list view of Module 4 keeps showing this placeholder for diamonds because
 * bulk decryption is too expensive — see /tcn/me/creators page docstring.
 */
export const ENCRYPTED_PLACEHOLDER = '—';

/**
 * Returns true if a value is a non-null, finite number.
 */
export const isNum = (v) => typeof v === 'number' && Number.isFinite(v);
